/**
 * Validation for the client portal — Phase 11 (decisions D146–D149).
 *
 * Two families live here, and they never share a controller:
 *
 *  - STAFF-SIDE: a laboratory member with `portal_accounts.manage` creating,
 *    scoping, deactivating and inviting portal accounts, and a member with
 *    `reports.deliver` releasing a report version to the portal or
 *    withdrawing it.
 *
 *  - PORTAL-SIDE: what a portal caller may send to `/api/v1/portal/**` —
 *    signing in, refreshing, resetting a password, choosing a language, and
 *    paging through its own lists.
 *
 * EVERY BODY SCHEMA IS STRICT, AND SO IS EVERY PORTAL QUERY. A portal caller
 * never names a client, a contact, an account or a laboratory: those come from
 * its token and its account row, server-side (CLAUDE.md section 5). A
 * `clientId` in a portal body or query is refused as an unknown key here, and
 * the tenant selector guard refuses it again on the route.
 */
import { z } from 'zod';
import { ALL_PORTAL_PROJECT_SCOPES, PAGINATION_DEFAULTS, PORTAL_DEFAULT_VISIBILITIES, PORTAL_PROJECT_SCOPE, } from '@lims/shared-types';
import { emailSchema, localeSchema, newPasswordSchema, reasonSchema, uuidSchema, } from './primitives.js';
import { statementQuerySchema } from './finance.js';
// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------
/** An optional free-text field: absent, or actual content. Never blank. */
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((value) => (value === '' ? null : value));
export const portalProjectScopeSchema = z.enum(ALL_PORTAL_PROJECT_SCOPES);
/** A laboratory's DEFAULT visibility for a newly enabled contact: never
 *  `none` (D146). */
export const portalDefaultVisibilitySchema = z.enum(PORTAL_DEFAULT_VISIBILITIES);
const MAX_PORTAL_PROJECTS = 200;
const projectIdsSchema = z
    .array(uuidSchema)
    .max(MAX_PORTAL_PROJECTS)
    .refine((ids) => new Set(ids).size === ids.length, {
    message: 'Each project appears once',
});
/**
 * The scope and its projects describe one decision (D146):
 * `selected_projects` NAMES at least one project; `all_projects` names none.
 * With no scope in the body the backend applies the laboratory's default —
 * or, on an update, keeps the account's — and checks the pair itself.
 */
function scopeMatchesProjects(value) {
    if (value.projectScope === PORTAL_PROJECT_SCOPE.SELECTED_PROJECTS) {
        return (value.projectIds?.length ?? 0) > 0;
    }
    if (value.projectScope === PORTAL_PROJECT_SCOPE.ALL_PROJECTS) {
        return (value.projectIds?.length ?? 0) === 0;
    }
    return true;
}
const SCOPE_REFINEMENT = {
    message: 'Selected projects name at least one project; all projects name none',
    path: ['projectIds'],
};
// ---------------------------------------------------------------------------
// Staff side: portal accounts (contract 5.7)
// ---------------------------------------------------------------------------
/**
 * Create the portal account of one contact. The address is the CONTACT'S,
 * never supplied here. Absent, the scope is the laboratory's
 * `portal_default_project_scope` and the language its `default_locale` —
 * both applied by the backend (CLAUDE.md section 6.1).
 */
export const createPortalAccountSchema = z
    .object({
    projectScope: portalProjectScopeSchema.optional(),
    projectIds: projectIdsSchema.optional(),
    preferredLocale: localeSchema.optional(),
})
    .strict()
    .refine(scopeMatchesProjects, SCOPE_REFINEMENT);
/** Change an account's scope, projects, language or address. `projectIds`,
 *  when given, REPLACES the set. */
export const updatePortalAccountSchema = z
    .object({
    projectScope: portalProjectScopeSchema.optional(),
    projectIds: projectIdsSchema.optional(),
    preferredLocale: localeSchema.optional(),
    email: emailSchema.optional(),
})
    .strict()
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Nothing to change',
})
    .refine(scopeMatchesProjects, SCOPE_REFINEMENT);
/** Deactivate or reactivate an account. Deactivation revokes every live
 *  portal session and touches none of the client's records. */
export const portalAccountReasonSchema = z.object({ reason: reasonSchema }).strict();
// ---------------------------------------------------------------------------
// Staff side: releasing a report version to the portal (D149)
// ---------------------------------------------------------------------------
/** Release the CURRENT version. Issuance is not release, and release is not
 *  delivery. */
export const reportPortalReleaseSchema = z
    .object({ note: optionalText(2000) })
    .strict();
/** Withdraw the released version — always with a reason. */
export const reportPortalWithdrawSchema = z.object({ reason: reasonSchema }).strict();
// ---------------------------------------------------------------------------
// Portal side: authentication (D147)
// ---------------------------------------------------------------------------
/** Sign in. Every failure before the credentials are proven is the same
 *  `INVALID_CREDENTIALS`, whatever the address belongs to. */
export const portalLoginSchema = z
    .object({
    email: emailSchema,
    // Bounded, not policed: the identity provider owns the password.
    password: z.string().min(1).max(256),
})
    .strict();
export const portalRefreshSchema = z
    .object({ refreshToken: z.string().min(1).max(512) })
    .strict();
/** Always answered `202` with the same body, whether or not the address has
 *  an account (no enumeration). */
export const portalPasswordResetRequestSchema = z
    .object({ email: emailSchema })
    .strict();
/** The same password policy as staff. */
export const portalPasswordResetConfirmSchema = z
    .object({
    token: z.string().min(1).max(512),
    password: newPasswordSchema,
})
    .strict();
export const portalUpdateMeSchema = z
    .object({ preferredLocale: localeSchema })
    .strict();
// ---------------------------------------------------------------------------
// Portal side: queries — strict, and never naming a client
// ---------------------------------------------------------------------------
const portalPageShape = {
    page: z.coerce.number().int().positive().default(PAGINATION_DEFAULTS.PAGE),
    pageSize: z.coerce
        .number()
        .int()
        .positive()
        .max(PAGINATION_DEFAULTS.MAX_PAGE_SIZE)
        .default(PAGINATION_DEFAULTS.PAGE_SIZE),
};
/** Page through a portal list with no filter (reports). */
export const portalListQuerySchema = z.object(portalPageShape).strict();
/** The client's requests, filtered on the DERIVED status. */
export const portalWorkOrderListQuerySchema = z
    .object({
    ...portalPageShape,
    status: z
        .enum(['awaiting_sample', 'in_progress', 'partially_complete', 'complete', 'cancelled'])
        .optional(),
})
    .strict();
export const portalReportListQuerySchema = portalListQuerySchema;
/** Issued and cancelled invoices only — a portal never sees a draft. */
export const portalInvoiceListQuerySchema = z
    .object({
    ...portalPageShape,
    status: z.enum(['issued', 'cancelled']).optional(),
})
    .strict();
/** The SAME parameters as the staff statement (D155), strict. */
export const portalStatementQuerySchema = statementQuerySchema.innerType().strict().refine((value) => value.from === undefined || value.to === undefined || value.to >= value.from, { message: 'A statement period ends on or after the day it starts', path: ['to'] });
//# sourceMappingURL=portal.js.map